import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { useStartNewChat } from '@features/chat';
import { useSidebarNavigation } from './hooks/useSidebarNavigation';

/**
 * Individual navigation item with support for both expanded and collapsed states
 * @param {Object} props
 * @param {string} props.path - Route path
 * @param {string} props.labelKey - i18n translation key for the label
 * @param {string} props.icon - Iconify icon name
 */
export function SidebarNavItem({ path, labelKey, icon }) {
  const { t } = useTranslation();
  const { isActive } = useSidebarNavigation();
  const startNewChat = useStartNewChat();

  const active = isActive(path);

  const baseClasses = 'min-h-[36px] flex items-center py-2 px-2.5 text-sm rounded-lg hover:bg-brand/5 focus:outline-none focus:bg-brand/5 relative';
  const activeClasses = 'bg-brand/5 text-brand before:absolute before:-left-3 before:top-0 before:h-full before:w-[3px] before:rounded-r-md';
  const inactiveClasses = 'text-muted';

  // Special handler for "New chat" button
  const handleNewChat = async (e) => {
    e.preventDefault();
    await startNewChat();
  };

  // If this is the "New chat" button, use button instead of link
  if (path === '/') {
    return (
      <li>
        {/* Button for expanded sidebar */}
        <button
          onClick={handleNewChat}
          className={`hs-overlay-minified:hidden gap-x-3.5 ${baseClasses} ${inactiveClasses} w-full text-left cursor-pointer`}
        >
          <div className="shrink-0 flex items-center justify-center size-6 rounded-full bg-primary">
            <Icon icon={icon} className="size-3 text-white" />
          </div>
          <span>{t(labelKey)}</span>
        </button>

        {/* Button with tooltip for collapsed sidebar */}
        <div className="hidden hs-overlay-minified:block hs-tooltip [--placement:right] [--trigger:hover]">
          <button
            onClick={handleNewChat}
            className={`justify-center ${baseClasses} ${inactiveClasses} w-full cursor-pointer`}
          >
            <div className="shrink-0 flex items-center justify-center size-6 rounded-full bg-primary">
              <Icon icon={icon} className="size-3 text-white" />
            </div>
          </button>
          <span
            className="hs-tooltip-content hs-tooltip-shown:opacity-100 hs-tooltip-shown:visible opacity-0 transition-opacity inline-block absolute invisible z-[100] py-2 px-3 bg-white text-muted text-sm font-medium rounded-lg shadow-lg border border-zinc-200 whitespace-nowrap"
            role="tooltip"
          >
            {t(labelKey)}
          </span>
        </div>
      </li>
    );
  }

  return (
    <li>
      {/* Link for expanded sidebar */}
      <Link
        to={path}
        className={`hs-overlay-minified:hidden gap-x-3.5 ${baseClasses} ${active ? activeClasses : inactiveClasses}`}
      >
        <Icon icon={icon} className="shrink-0 size-5" />
        <span>{t(labelKey)}</span>
        {active && (
          <div className="absolute -left-3 top-0 h-full w-[3px] rounded-r-md" style={{ backgroundColor: 'var(--color-primary)' }} />
        )}
      </Link>

      {/* Link with tooltip for collapsed sidebar */}
      <div className="hidden hs-overlay-minified:block hs-tooltip [--placement:right] [--trigger:hover]">
        <Link
          to={path}
          className={`justify-center ${baseClasses} ${active ? activeClasses : inactiveClasses}`}
        >
          <Icon icon={icon} className="shrink-0 size-5" />
          {active && (
            <div className="absolute -left-3 top-0 h-full w-[3px] rounded-r-md" style={{ backgroundColor: 'var(--color-primary)' }} />
          )}
        </Link>
        <span
          className="hs-tooltip-content hs-tooltip-shown:opacity-100 hs-tooltip-shown:visible opacity-0 transition-opacity inline-block absolute invisible z-[100] py-2 px-3 bg-white text-muted text-sm font-medium rounded-lg shadow-lg border border-zinc-200 whitespace-nowrap"
          role="tooltip"
        >
          {t(labelKey)}
        </span>
      </div>
    </li>
  );
}
